import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';

import { paths } from 'src/routes/paths';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { parseHighlightTerms } from 'src/utils/highlight-terms';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { DocumentType } from 'src/services/files/file.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { openSingleMailerDialog } from 'src/store/slices/app/appSlice';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';
import { useGetCandidateSummaryFieldsQuery } from 'src/services/settings/candidate-summary-fields.service';
import {
  useGetCandidateDocumentsQuery,
  useGetOrganizationCandidateQuery,
  useUpdateCandidateRatingMutation,
  useSetCandidateProfileMutation,
} from 'src/services/candidates/candidates.service';
import { isAvailabilityFeatureLocked, generateCandidateProfileErrorMessage } from 'src/services/candidates/candidates.utils';

import StarRating from 'src/components/rating/star-rating';
import HighlightedText from 'src/components/highlight-text/highlight-text';
import CandidateResumeView from 'src/components/candidates/candidate-resume-view';
import AvailabilityStatusLabel from 'src/components/candidates/availability-status-label';

import AddItemsModal from 'src/sections/lists/modals/add-items-modal';
import AddCandidateJobModal from 'src/sections/jobs/modals/add-candidate-job-modal';
import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';
import CandidateProfileEditor from 'src/sections/profile/profile-sections/profile-editor';
import CandidateDocumentsView from 'src/sections/candidates/detail/sections/candidate-documents';
import { isSummaryTitleSelected, buildCandidateSummaryMeta } from 'src/sections/candidates/candidate-summary-meta';
import { buildDialNumber, useCommunicationActions } from 'src/sections/admin/integrations/communications/use-communication-actions';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

import RecordActionBar from '../record-action-bar';
import RecordPanelHeader from '../record-panel-header';
import { RecordAction, RecordMetaItem, RecordHeaderModel } from '../record-panel.types';

// ----------------------------------------------------------------------

enum TabValue {
  PROFILE = 'profile',
  RESUME = 'resume',
  DOCUMENTS = 'documents',
}

/**
 * Candidate body for the record panel (JT1-1529). Fetches the CRM record and feeds the shared header
 * (avatar, name + availability, job title, and a location/salary/notice/rank metadata row) and action
 * bar (Add to job, Add to list, Send email, Call — all labelled buttons). The body defaults to the
 * resume: the parsed `resume_text` when present, falling back to the PDF viewer, then the profile.
 */
export default function CandidateRecordView({ id }: { id: string }) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const tenantType = useOrgTenant();
  const { featureEnabled } = useFeatureFlags();

  const { data: candidate, isLoading } = useGetOrganizationCandidateQuery(id, { skip: !id });
  const profile = candidate?.candidate_profile;

  // Org-configurable summary-line fields (spec item 17, JT1-1538), shared with the profile header.
  const { data: summaryFieldsData } = useGetCandidateSummaryFieldsQuery();
  const summaryFieldKeys = summaryFieldsData?.fields;

  // Resume lives on the ORG-CANDIDATE document store (organization_candidate_documents), same source
  // the full detail page uses — not the profile store (cp_documents), which is empty for candidates
  // whose CV was never cloned onto the attached profile (e.g. pre-item-33 records). Filter to the
  // resume type server-side.
  const { data: resumeDocs } = useGetCandidateDocumentsQuery(
    { candidate_id: id, params: { page: 1, per_page: 1, type: DocumentType.RESUME } },
    { skip: !id }
  );
  const resumeDoc = resumeDocs?.results?.[0];

  const hasResume = Boolean(candidate?.resume_text) || Boolean(resumeDoc);

  // Search-term highlighting when the record was opened from a search (?q=…, spec item 18, JT1-1537).
  // A highlight term flips the resume body to the parsed-text view so the hits are visible.
  const [searchParams] = useSearchParams();
  const highlightTerms = useMemo(() => parseHighlightTerms(searchParams.get('q')), [searchParams]);

  const [currentTab, setCurrentTab] = useState<TabValue>(TabValue.PROFILE);
  // Default to the resume once we know one exists; otherwise the profile.
  useEffect(() => {
    if (!candidate) return;
    setCurrentTab(hasResume ? TabValue.RESUME : TabValue.PROFILE);
  }, [candidate, hasResume]);

  // Recruiter-only avatar upload — writes the photo to the durable attached (CRM) profile; the live
  // profile is never touched (JT1-1539). The OrgCandidates tag invalidation refreshes this panel.
  const [updateProfile] = useSetCandidateProfileMutation();
  const handlePhotoChange = useCallback(async (fileId: string | null) => {
    if (!profile?.id) return;
    try {
      await updateProfile({ id: profile.id, data: { profile_image_id: fileId } }).unwrap();
    } catch (e) {
      enqueueSnackbar(generateCandidateProfileErrorMessage(e), { variant: 'error' });
    }
  }, [updateProfile, profile?.id]);

  const [updateRating] = useUpdateCandidateRatingMutation();
  const handleRating = useCallback(async (rating: number) => {
    try {
      await updateRating({ candidateId: id, rating }).unwrap();
      enqueueSnackbar(t('candidates.api.update_rating.success'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('candidates.api.update_rating.default_error'), { variant: 'error' });
    }
  }, [updateRating, id, t]);

  // Actions
  const addToJob = useBoolean();
  const addToList = useBoolean();

  const commsName = `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim();
  const commsNumber = candidate?.privileged_access ? buildDialNumber(candidate?.phone_number ?? undefined, candidate?.country_code ?? undefined) : undefined;
  const comms = useCommunicationActions(commsNumber, commsName, { entity_type: CommunicationEntityType.CANDIDATE, entity_id: id });

  const canEmail = featureEnabled(FeatureType.EMAIL_CONNECTOR_ENABLED) && tenantType !== TenantType.Candidate && Boolean(candidate?.email);
  const handleSendEmail = useCallback(() => {
    if (!candidate?.email) return;
    dispatch(openSingleMailerDialog({
      emails: [candidate.email],
      templateType: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
      templateEntityId: id,
      targetType: MailerTargetType.CANDIDATE,
      targetId: id,
    }));
  }, [candidate?.email, dispatch, id]);

  const actions: RecordAction[] = useMemo(() => [
    { key: 'add-to-job', label: t('common.add_to_job'), icon: 'mdi:invite', onClick: addToJob.onTrue },
    { key: 'add-to-list', label: t('common.add_to_list', { defaultValue: 'Add to list' }), icon: 'solar:list-bold', onClick: addToList.onTrue },
    { key: 'send-email', label: t('common.send_email'), icon: 'lsicon:email-send-filled', onClick: handleSendEmail, hidden: !canEmail },
    { key: 'call', label: t('integrations.communications.actions.call'), icon: 'solar:phone-bold', onClick: () => comms.call(), hidden: !comms.commsActive },
  ], [t, addToJob.onTrue, addToList.onTrue, handleSendEmail, canEmail, comms]);

  const header: RecordHeaderModel = useMemo(() => {
    // Org-configurable summary line shared with the profile header (spec item 17); rank stays
    // panel-specific because it is interactive. `title`, when selected, drives the subtitle below.
    // Location falls back to the live/synced profile when the CRM location is empty (JT1-1535).
    // Text values highlight the search terms the record was opened with (JT1-1693).
    const meta: RecordMetaItem[] = buildCandidateSummaryMeta(profile, t, summaryFieldKeys, candidate?.synced_profile?.location)
      .map((item) => (typeof item.value === 'string'
        ? { ...item, value: <HighlightedText text={item.value} terms={highlightTerms} /> }
        : item));
    meta.push({ key: 'rank', label: t('candidates.table.rating'), value: <StarRating rating={candidate?.rating || 0} onSelectExcitementRating={handleRating} /> });

    // Carry the search terms onto the full-profile link so its Resume tab highlights the same hits.
    const detailHref = paths.dashboard.candidates.detail(id);
    const q = searchParams.get('q');

    return {
      avatarUrl: profile?.profile_image?.public_path,
      name: `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim() || t('common.loading', { defaultValue: 'Loading…' }),
      avatar: tenantType === TenantType.Recruiter
        ? { editable: true, cropTitle: t('components.entity_avatar.candidate_photo_title'), onChange: handlePhotoChange }
        : undefined,
      subtitle: isSummaryTitleSelected(summaryFieldKeys) && profile?.position_title
        ? <HighlightedText text={profile.position_title} terms={highlightTerms} />
        : undefined,
      // Shown even when unset: the label renders that as a grey "Not shared" chip, which is the
      // honest answer for a candidate who has never shared their availability (JT1-1620). Hidden
      // while the profile is still loading, and when the feature gate has stripped the field.
      status: profile && !isAvailabilityFeatureLocked(profile.availability_status)
        ? <AvailabilityStatusLabel status={profile.availability_status} />
        : undefined,
      meta,
      contact: candidate?.privileged_access
        ? { email: candidate?.email, phone: candidate?.phone_number ? `${candidate.country_code ?? ''} ${candidate.phone_number}`.trim() : null }
        : undefined,
      fullProfileHref: q ? `${detailHref}?q=${encodeURIComponent(q)}` : detailHref,
      highlightTerms,
    };
  }, [profile, candidate, t, handleRating, id, searchParams, tenantType, handlePhotoChange, summaryFieldKeys, highlightTerms]);

  if (isLoading || !candidate) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1, py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <RecordPanelHeader model={header} />
      <RecordActionBar actions={actions} />

      <Tabs
        value={currentTab}
        onChange={(_e, tab) => setCurrentTab(tab)}
        sx={{ px: 2.5, mt: 1, boxShadow: (theme) => `inset 0 -2px 0 0 ${theme.palette.divider}` }}
      >
        <Tab value={TabValue.PROFILE} label={t('candidates.drawer.tabs.profile', { defaultValue: 'Profile' })} />
        {hasResume && <Tab value={TabValue.RESUME} label={t('candidates.drawer.tabs.resume', { defaultValue: 'Resume' })} />}
        <Tab value={TabValue.DOCUMENTS} label={t('candidates.drawer.tabs.documents', { defaultValue: 'Documents' })} />
      </Tabs>

      {currentTab === TabValue.PROFILE && (
        <Stack sx={{ px: 3, py: 2 }}>
          <CandidateProfileEditor profile={profile} isLoading={isLoading} mode="view" showNavigation={false} commsEntityId={candidate.id} highlightTerms={highlightTerms} />
        </Stack>
      )}

      {currentTab === TabValue.RESUME && (
        <Stack sx={{ px: 3, py: 2, flexGrow: 1, minHeight: 0 }}>
          <CandidateResumeView
            resumeText={candidate?.resume_text}
            resumeDocUrl={resumeDoc?.document?.public_path}
            highlightTerms={highlightTerms}
            prefer="document"
          />
        </Stack>
      )}

      {currentTab === TabValue.DOCUMENTS && (
        <Stack sx={{ px: 3, py: 2 }}>
          <CandidateDocumentsView candidateId={id} uploadVisible={false} />
        </Stack>
      )}

      <AddCandidateJobModal open={addToJob.value} onClose={addToJob.onFalse} candidateId={id} />
      <AddItemsModal open={addToList.value} onClose={addToList.onFalse} onSuccess={addToList.onFalse} items={[id]} entityType={ListEntityType.CANDIDATE} />
      {comms.smsModal}
    </>
  );
}
